import { useCallback, useEffect, useMemo, useState } from 'react';
import type { UserManager } from 'oidc-client-ts';
import {
  ApiError, createClient, devLogin, fetchConfig,
  type AuditEvent, type Me, type Member, type PublicConfig, type Risk,
} from './api';
import { createCognitoManager, DEV_USERS } from './auth';
import { RiskForm, type RiskInput } from './RiskForm';

const TOKEN_KEY = 'hr.devToken';

function issuesToErrors(err: unknown): Record<string, string> {
  if (err instanceof ApiError && err.body.issues) {
    return Object.fromEntries(err.body.issues.map((i) => [String(i.path[0] ?? 'form'), i.message]));
  }
  return { form: err instanceof Error ? err.message : 'Request failed' };
}

export function App() {
  const [config, setConfig] = useState<PublicConfig | null>(null);
  const [token, setToken] = useState<string | null>(() => sessionStorage.getItem(TOKEN_KEY));
  const [oidc, setOidc] = useState<UserManager | null>(null);

  useEffect(() => {
    fetchConfig().then(async (cfg) => {
      setConfig(cfg);
      if (cfg.authMode === 'cognito' && cfg.cognito) {
        const mgr = createCognitoManager(cfg.cognito);
        setOidc(mgr);
        if (window.location.pathname === '/auth/callback') {
          const user = await mgr.signinRedirectCallback();
          window.history.replaceState({}, '', '/');
          setToken(user.access_token);
        } else {
          const user = await mgr.getUser();
          if (user && !user.expired) setToken(user.access_token);
        }
      }
    });
  }, []);

  const signOut = () => {
    sessionStorage.removeItem(TOKEN_KEY);
    setToken(null);
    oidc?.signoutRedirect();
  };

  if (!config) return <main className="shell">Loading…</main>;
  if (!token) {
    return (
      <main className="shell login">
        <h1>Harbour Risk</h1>
        <p className="muted">Synthetic training SaaS · release {config.release}</p>
        {config.authMode === 'cognito' ? (
          <button onClick={() => oidc?.signinRedirect()}>Sign in</button>
        ) : (
          <>
            <p className="banner">Local development sign-in — synthetic users only. Disabled in deployed environments.</p>
            <ul className="dev-users">
              {DEV_USERS.map(([email, label]) => (
                <li key={email}>
                  <button
                    className="secondary"
                    onClick={async () => {
                      const tok = await devLogin(email);
                      sessionStorage.setItem(TOKEN_KEY, tok);
                      setToken(tok);
                    }}
                  >
                    {email} <span className="muted">({label})</span>
                  </button>
                </li>
              ))}
            </ul>
          </>
        )}
      </main>
    );
  }
  return <Workspace token={token} release={config.release} onSignOut={signOut} />;
}

function Workspace({ token, release, onSignOut }: { token: string; release: string; onSignOut(): void }) {
  const client = useMemo(() => createClient(() => token), [token]);
  const [me, setMe] = useState<Me | null>(null);
  const [tenantId, setTenantId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    client.me().then((m) => {
      setMe(m);
      setTenantId(m.memberships[0]?.tenantId ?? null);
    }).catch((e) => (e instanceof ApiError && e.status === 401 ? onSignOut() : setError(String(e))));
  }, [client, onSignOut]);

  if (error) return <main className="shell"><p className="error">{error}</p></main>;
  if (!me) return <main className="shell">Loading…</main>;
  const membership = me.memberships.find((m) => m.tenantId === tenantId);

  return (
    <main className="shell">
      <header className="topbar">
        <strong>Harbour Risk</strong>
        {me.memberships.length > 0 && (
          <select aria-label="Organisation" value={tenantId ?? ''} onChange={(e) => setTenantId(e.target.value)}>
            {me.memberships.map((m) => <option key={m.tenantId} value={m.tenantId}>{m.name}</option>)}
          </select>
        )}
        <span className="muted">{me.user.displayName} · {membership?.role ?? 'no access'} · {release}</span>
        <button className="secondary" onClick={onSignOut}>Sign out</button>
      </header>
      {membership ? <TenantView key={membership.tenantId} client={client} membership={membership} /> : (
        <p>You have no active organisation memberships.</p>
      )}
    </main>
  );
}

function TenantView({ client, membership }: { client: ReturnType<typeof createClient>; membership: Me['memberships'][number] }) {
  const [risks, setRisks] = useState<Risk[]>([]);
  const [members, setMembers] = useState<Member[]>([]);
  const [audit, setAudit] = useState<AuditEvent[] | null>(null);
  const [editing, setEditing] = useState<Risk | 'new' | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const canWrite = membership.role !== 'auditor';
  const canAudit = membership.role !== 'member';
  const t = membership.tenantId;

  const reload = useCallback(async () => {
    const [r, m] = await Promise.all([client.risks(t), client.members(t)]);
    setRisks(r.risks);
    setMembers(m.members);
    if (canAudit) setAudit((await client.audit(t)).events);
  }, [client, t, canAudit]);

  useEffect(() => { reload(); }, [reload]);

  async function save(input: RiskInput) {
    try {
      if (editing === 'new') await client.createRisk(t, input);
      else if (editing) await client.updateRisk(t, editing.id, input);
      setErrors({});
      setEditing(null);
      await reload();
    } catch (e) {
      setErrors(issuesToErrors(e));
    }
  }

  const ownerName = (id: string | null) => members.find((m) => m.id === id)?.displayName ?? '—';

  return (
    <section>
      <div className="row between">
        <h2>{membership.name} risk register</h2>
        {canWrite && !editing && <button onClick={() => setEditing('new')}>New risk</button>}
      </div>
      {editing && (
        <RiskForm
          members={members}
          initial={editing === 'new' ? undefined : editing}
          serverErrors={errors}
          onSubmit={save}
          onCancel={() => { setEditing(null); setErrors({}); }}
        />
      )}
      {errors.form && <p className="error" role="alert">{errors.form}</p>}
      <table>
        <thead><tr><th>Title</th><th>L</th><th>I</th><th>Rating</th><th>Residual</th><th>Status</th><th>Owner</th><th /></tr></thead>
        <tbody>
          {risks.map((r) => (
            <tr key={r.id}>
              <td>{r.title}</td><td>{r.likelihood}</td><td>{r.impact}</td>
              <td><span className={`rating r${Math.ceil(r.score / 5)}`}>{r.score}</span></td>
              <td>{r.residualScore === null ? '—' : <span className={`rating r${Math.ceil(r.residualScore / 5)}`}>{r.residualScore}</span>}</td>
              <td>{r.status}</td><td>{ownerName(r.ownerUserId)}</td>
              <td className="actions">
                {canWrite && <button className="secondary" onClick={() => setEditing(r)}>Edit</button>}
                {membership.role === 'admin' && (
                  <button className="danger" onClick={async () => { await client.deleteRisk(t, r.id); await reload(); }}>Delete</button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {audit && (
        <>
          <h3>Activity</h3>
          <ol className="audit">
            {audit.slice(0, 20).map((e) => (
              <li key={e.id}><code>{e.action}</code> · {ownerName(e.actorUserId)} · {new Date(e.occurredAt).toLocaleString('en-AU')}</li>
            ))}
          </ol>
        </>
      )}
    </section>
  );
}
